import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Database, FlaskConical } from 'lucide-react';
import { api } from '../../lib/api';
import { Link } from 'react-router-dom';
import { Button, Card, Field, Input, Pill } from '../../components/ui';
import type { StorageView } from './StorageCredentials';

/**
 * Where the platform keeps its files — firmware images, bank-transfer
 * proofs, whatever comes next. Local directory by default; S3-compatible
 * (AWS, MinIO, Vultr, Cloudflare R2) or bunny.net when the operator has
 * object storage.
 *
 * Configuration only: driver, endpoint, region, bucket, zone. The KEYS live
 * on the API keys page with every other secret the platform holds (the
 * operator's call) — this card links there. The Test button stays here,
 * because it proves the whole store works, not just the credentials.
 */
export function StorageCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['storage', 'settings'],
    queryFn: () => api.get<StorageView>('/storage/settings'),
  });
  const [form, setForm] = useState<Record<string, string>>({});
  const [driver, setDriver] = useState<'local' | 's3' | 'bunny' | null>(null);
  const [pathStyle, setPathStyle] = useState<boolean | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A relocation asks for the password before it is sent — the server
  // requires it, and asking first beats a refusal after the click.
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');

  const seeded = useRef(false);
  useEffect(() => {
    if (!query.data || seeded.current) return;
    seeded.current = true;
    const d = query.data;
    setForm({
      localDir: d.local.dir,
      s3Endpoint: d.s3.endpoint,
      s3Region: d.s3.region,
      s3Bucket: d.s3.bucket,
      bunnyHost: d.bunny.host,
      bunnyZone: d.bunny.zone,
    });
  }, [query.data]);

  const save = useMutation({
    mutationFn: (password?: string) => {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(form)) {
        if (v !== '') body[k] = v;
      }
      if (driver) body.driver = driver;
      if (pathStyle !== null) body.s3ForcePathStyle = pathStyle;
      if (password) body.password = password;
      return api.put('/storage/settings', body);
    },
    onSuccess: async () => {
      setError(null);
      setNotice('Saved.');
      setConfirming(false);
      setPassword('');
      setTimeout(() => setNotice(null), 4000);
      await queryClient.invalidateQueries({ queryKey: ['storage'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const test = useMutation({
    mutationFn: () => api.post<{ ok: boolean; kind: string; detail: string }>('/storage/test'),
    onSuccess: (r) => {
      setError(r.ok ? null : `${r.kind}: ${r.detail}`);
      setNotice(r.ok ? `${r.kind}: ${r.detail}` : null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Test failed'),
  });

  const s = query.data;
  if (!s) return null;
  const activeDriver = driver ?? s.driver;
  const set = (k: string) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });

  // Which fields decide WHERE files live — the server gates exactly these.
  const relocating =
    (driver !== null && driver !== s.driver) ||
    (pathStyle !== null && pathStyle !== s.s3.forcePathStyle) ||
    (form.localDir ?? '') !== s.local.dir ||
    (form.s3Endpoint ?? '') !== s.s3.endpoint ||
    (form.s3Region ?? '') !== s.s3.region ||
    (form.s3Bucket ?? '') !== s.s3.bucket ||
    (form.bunnyHost ?? '') !== s.bunny.host ||
    (form.bunnyZone ?? '') !== s.bunny.zone;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Database className="size-4 text-muted" />
          File storage
        </span>
      }
      action={
        <Button
          size="sm"
          variant="ghost"
          icon={<FlaskConical className="size-3.5" />}
          loading={test.isPending}
          onClick={() => test.mutate()}
        >
          Test the store
        </Button>
      }
    >
      <div className="space-y-4 p-5">
        <p className="text-sm text-muted">
          Firmware images and uploaded files live here. Local directory by default; S3-compatible
          object storage (AWS, MinIO, Vultr, Cloudflare R2) or bunny.net when you have it.
        </p>
        <div className="flex flex-wrap gap-2">
          {(['local', 's3', 'bunny'] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDriver(d)}
              className={`rounded-lg border px-4 py-2 text-sm transition-colors ${
                activeDriver === d
                  ? 'border-primary bg-primary-soft text-primary'
                  : 'border-line text-muted hover:text-ink'
              }`}
            >
              {d === 'local' ? 'Local directory' : d === 's3' ? 'S3-compatible' : 'bunny.net'}
              {d === 's3' && s.configured.s3 && ' ✓'}
              {d === 'bunny' && s.configured.bunny && ' ✓'}
            </button>
          ))}
        </div>

        {activeDriver === 'local' && (
          <Field label={`Directory (default ${s.defaultLocalDir}; firmware goes under <dir>/firmware)`}>
            <Input value={form.localDir ?? ''} onChange={set('localDir')} className="font-mono" />
          </Field>
        )}

        {activeDriver === 's3' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Endpoint (https://s3.amazonaws.com, MinIO/Vultr/R2 URL)">
              <Input value={form.s3Endpoint ?? ''} onChange={set('s3Endpoint')} className="font-mono" placeholder="https://…" />
            </Field>
            <Field label="Region">
              <Input value={form.s3Region ?? ''} onChange={set('s3Region')} className="font-mono" placeholder="us-east-1 (R2: auto)" />
            </Field>
            <Field label="Bucket">
              <Input value={form.s3Bucket ?? ''} onChange={set('s3Bucket')} className="font-mono" />
            </Field>
            <label className="flex cursor-pointer items-center gap-2 pt-6 text-sm text-ink">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={pathStyle ?? s.s3.forcePathStyle}
                onChange={(e) => setPathStyle(e.target.checked)}
              />
              Path-style URLs (MinIO, Vultr, R2 — off only for plain AWS)
            </label>
          </div>
        )}

        {activeDriver === 'bunny' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Storage host (region-specific, e.g. ny.storage.bunnycdn.com)">
              <Input value={form.bunnyHost ?? ''} onChange={set('bunnyHost')} className="font-mono" />
            </Field>
            <Field label="Storage zone">
              <Input value={form.bunnyZone ?? ''} onChange={set('bunnyZone')} className="font-mono" />
            </Field>
          </div>
        )}

        {activeDriver !== 'local' && (
          <div className="space-y-2">
            <p className="text-sm text-muted">
              The access keys for this driver live on the{' '}
              <Link className="text-primary hover:underline" to="/settings/api-keys">
                API keys page
              </Link>
              , with every other secret the platform holds.{' '}
              <Pill state tone={activeDriver === 's3' ? (s.configured.s3 ? 'success' : 'warn') : s.configured.bunny ? 'success' : 'warn'}>
                {(activeDriver === 's3' ? s.configured.s3 : s.configured.bunny)
                  ? 'keys configured'
                  : 'keys not set yet'}
              </Pill>
            </p>
            <p className="text-xs text-faint">
              bunny.net is not S3-compatible — it has its own driver here. Test after saving.
            </p>
          </div>
        )}
        {error && <p className="text-sm font-medium text-danger">{error}</p>}
        {notice && <p className="text-sm text-success">{notice}</p>}
      </div>
      {confirming && (
        <form
          className="flex flex-wrap items-end gap-3 border-t border-line px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(password);
          }}
        >
          <Field label="Your password — moving the store is re-authorised and audited">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              required
            />
          </Field>
          <Button type="submit" variant="primary" size="sm" loading={save.isPending}>
            Confirm
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setConfirming(false);
              setPassword('');
            }}
          >
            Cancel
          </Button>
        </form>
      )}
      <footer className="flex items-center justify-between gap-3 border-t border-line px-5 py-3.5">
        <span className="text-xs text-faint">
          {relocating
            ? 'This changes where files are stored — it asks for your password.'
            : 'Where files live is password-protected; keys live on the API keys page.'}
        </span>
        <Button
          variant="primary"
          loading={save.isPending && !confirming}
          onClick={() => (relocating ? setConfirming(true) : save.mutate(undefined))}
        >
          {relocating ? 'Move the store…' : 'Save'}
        </Button>
      </footer>
    </Card>
  );
}
